import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Video, Globe2, Play } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Settings → "Explainer videos". Keyword → video link. When a technician adds a
// quote line whose name contains the keyword, the video attaches itself.
// Vehicle Visuals share links (srvc.video/…) or YouTube both work.
export const ExplainerVideosPane = () => {
  const [data, setData] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [scope, setScope] = useState("workshop");
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState("");
  const [testResult, setTestResult] = useState(null);

  const load = () => api.get("/explainer-videos").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    setBusy(true);
    try {
      await api.post("/explainer-videos", { keyword, url, label, scope });
      toast.success(`"${keyword.trim().toLowerCase()}" now attaches a video automatically`);
      setKeyword(""); setUrl(""); setLabel(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    try { await api.delete(`/explainer-videos/${row.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
  };

  const runTest = async () => {
    if (!test.trim()) return;
    const r = await api.post("/explainer-videos/preview", { name: test });
    setTestResult(r.data.url || "none");
  };

  if (!data) return <div className="p-10 grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const Row = ({ row, global }) => (
    <div className="flex items-center gap-3 px-3 py-2 rounded-md border border-border bg-card/40" data-testid={`explainer-row-${row.id}`}>
      <Video className={`w-4 h-4 shrink-0 ${row.source === "vehicle_visuals" ? "text-[#7F95E8]" : "text-primary"}`} />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold">&ldquo;{row.keyword}&rdquo; {row.label && <span className="text-muted-foreground font-normal">· {row.label}</span>}</div>
        <a href={row.url} target="_blank" rel="noreferrer" className="text-[11px] font-mono-plex text-muted-foreground hover:text-primary truncate block">{row.url}</a>
      </div>
      {row.source === "vehicle_visuals" && <span className="text-[9px] font-black uppercase tracking-widest text-[#7F95E8]">Vehicle Visuals</span>}
      {(!global || data.can_edit_global) && (
        <button type="button" onClick={() => remove(row)} className="p-1.5 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive" title="Remove" data-testid={`explainer-remove-${row.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="settings-explainer-pane">
      <div className="rounded-lg border border-[#263B91]/50 bg-[#263B91]/10 p-4 text-xs text-muted-foreground leading-relaxed">
        <strong className="text-foreground">How it works.</strong> When a technician adds a quote line whose name contains a keyword below (e.g. &ldquo;brake pads&rdquo;), the matching video is attached and shown to the customer with a <span className="text-[#7F95E8] font-bold">Vehicle Visuals</span> credit. Paste a Vehicle Visuals share link (<span className="font-mono-plex">srvc.video/…</span>) or a YouTube link. Longer keywords win; your workshop&apos;s entries override the shared library.
      </div>

      <div className="rounded-lg border border-border p-4 space-y-3">
        <div className="label-overline">Add a video</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.6fr_1fr] gap-2">
          <input value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="keyword, e.g. brake pads" className="input-default text-sm" data-testid="explainer-keyword" />
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.srvc.video/…" className="input-default text-sm font-mono-plex" data-testid="explainer-url" />
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="label (optional)" className="input-default text-sm" data-testid="explainer-label" />
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {data.can_edit_global ? (
            <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <input type="checkbox" checked={scope === "global"} onChange={(e) => setScope(e.target.checked ? "global" : "workshop")} data-testid="explainer-scope-global" />
              <Globe2 className="w-3.5 h-3.5" /> Add to the shared library (every workshop)
            </label>
          ) : <span />}
          <button type="button" onClick={add} disabled={busy || keyword.trim().length < 3 || !url.trim()} className="btn-primary" data-testid="explainer-add-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add video
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-border p-4 space-y-2">
        <div className="label-overline">Try it — which video would attach?</div>
        <div className="flex gap-2">
          <input value={test} onChange={(e) => setTest(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runTest()} placeholder="Type a quote line, e.g. Front brake pads worn" className="input-default text-sm flex-1" data-testid="explainer-test-input" />
          <button type="button" onClick={runTest} className="btn-outline" data-testid="explainer-test-btn"><Play className="w-4 h-4" /> Test</button>
        </div>
        {testResult && (
          <div className="text-xs font-mono-plex" data-testid="explainer-test-result">
            {testResult === "none" ? <span className="text-amber-300">No video would attach — add a keyword above.</span> : <a className="text-primary" href={testResult} target="_blank" rel="noreferrer">{testResult}</a>}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="label-overline">Your workshop&apos;s videos ({data.workshop.length})</div>
        {data.workshop.length === 0 && <div className="text-xs text-muted-foreground px-1" data-testid="explainer-workshop-empty">None yet — the shared library below applies.</div>}
        {data.workshop.map((r) => <Row key={r.id} row={r} />)}
      </div>
      <div className="space-y-2">
        <div className="label-overline">Shared library ({data.global.length})</div>
        {data.global.map((r) => <Row key={r.id} row={r} global />)}
      </div>
    </div>
  );
};
